/**
 * @fileoverview Custom hook for refreshing user profile data.
 */

import {useQuery} from "react-query";
import {UserDto} from "../utils/types/user";
import Alert from "react-s-alert";

import {UserService} from "../services/UserService";
import {isUnauthorized} from "../services/api";
import {dispatch} from "../store";

/**
 * Custom hook for refreshing user profile data.
 * This query is disabled by default and must be triggered programmatically using refetch.
 * Handles success and error cases, including automatic logout on unauthorized errors.
 *
 * @param {Object} params The hook parameters.
 * @param {string} params.userId The ID of the user to refresh.
 * @param {Function} params.setUser Callback function to update the user data on success.
 * @returns {Object} React Query result object with refetch capability.
 */
export const useRefreshUserProfile = ({
  userId,
  setUser,
}: {
  userId?: string;
  setUser: (user: UserDto) => void;
}) =>
  useQuery(
    ["refreshUserProfile", userId],
    async ({queryKey: [_, userId]}) => {
      if (!userId) {
        return null;
      }
      const {data} = await UserService.find(userId);
      return data;
    },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      enabled: false, // this query is used programmatically by the `refetch`
      onSuccess: data => data && setUser(data),
      onError: (error: any) => {
        if (!isUnauthorized(error.status)) {
          Alert.error("Failed to refresh user profile.");
        } else {
          dispatch.adminSession.logout();
        }
      },
    },
  );
